import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck,
  FileText,
  Upload,
  Trash2,
  Loader2,
  AlertCircle,
  Eye,
  Calendar,
  Plus,
  X,
  CheckCircle2,
  Clock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAGS = [
  { value: 'tax_filing', label: 'Tax Filing' },
  { value: 'legal_agreement', label: 'Legal Agreement' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'annual_report', label: 'Annual Report' },
  { value: 'other', label: 'Other' },
];

const TAG_LABELS = Object.fromEntries(TAGS.map((t) => [t.value, t.label]));

const formatDate = (s) => {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '-';
  }
};

const formatBytes = (b) => {
  if (!b && b !== 0) return null;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadModal = ({ companyId, onClose, onUploaded, token }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const toggleTag = (t) => {
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) return setError('Title is required');
    if (!file) return setError('Please pick a file');

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('company_id', companyId);
      fd.append('title', title.trim());
      if (description.trim()) fd.append('description', description.trim());
      fd.append('tags', JSON.stringify(tags));
      fd.append('file', file);
      await axios.post(`${API}/admin/docvault`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="docvault-upload-modal-backdrop"
    >
      <div
        className="bg-[#0D1117] border border-gray-700 rounded-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="docvault-upload-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h2 className="text-lg font-bold text-white">Upload DocVault Document</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="docvault-upload-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Title <span className="text-red-400">*</span></label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Tax Filing 2025 — Form 1120"
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
              data-testid="docvault-upload-title"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional one-line context for the client"
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#00E87A] resize-none"
              data-testid="docvault-upload-description"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-2">Tags</label>
            <div className="flex flex-wrap gap-2">
              {TAGS.map((t) => {
                const active = tags.includes(t.value);
                return (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => toggleTag(t.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-[#00E87A] text-gray-900 border-[#00E87A]'
                        : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-500'
                    }`}
                    data-testid={`docvault-upload-tag-${t.value}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">File <span className="text-red-400">*</span></label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-gray-300 file:mr-3 file:px-3 file:py-2 file:bg-[#00E87A]/10 file:text-[#00E87A] file:border-0 file:rounded-lg file:cursor-pointer"
              data-testid="docvault-upload-file"
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white"
              data-testid="docvault-upload-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !title.trim() || !file}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E87A] text-gray-900 text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-50"
              data-testid="docvault-upload-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {submitting ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminDocVaultTab = ({ companyId, companyName }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const token = localStorage.getItem('auth_token');

  const fetchDocs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/docvault?company_id=${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocs(res.data?.documents || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleDelete = async (doc) => {
    if (!window.confirm(`Soft-delete "${doc.title}"? The S3 file is preserved.`)) return;
    setDeletingId(doc.id);
    try {
      await axios.delete(`${API}/admin/docvault/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchDocs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-docvault-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#00E87A]" />
            DocVault
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Documents you&apos;ve uploaded for {companyName || 'this company'}. Client gets emailed
            on upload and reminded on day 3, 7, and 14 until they download.
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E87A] text-gray-900 text-sm font-medium rounded-lg hover:opacity-90"
          data-testid="docvault-upload-open-btn"
        >
          <Plus className="w-4 h-4" />
          Upload Document
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-[#00E87A] animate-spin" />
        </div>
      ) : docs.length === 0 ? (
        <div
          className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-12 text-center"
          data-testid="admin-docvault-empty"
        >
          <ShieldCheck className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No DocVault documents yet</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-docvault-list">
          {docs.map((d) => {
            const size = formatBytes(d.file_size);
            return (
              <div
                key={d.id}
                className="bg-gray-800/30 border border-gray-700 rounded-lg p-4"
                data-testid={`admin-docvault-row-${d.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-[#00E87A]/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-[#00E87A]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-white font-semibold truncate">{d.title}</h3>
                      {d.description && (
                        <p className="text-gray-400 text-sm mt-1">{d.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {(d.tags || []).map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-700/50 text-gray-300"
                          >
                            {TAG_LABELS[t] || t}
                          </span>
                        ))}
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {formatDate(d.uploaded_at)}
                        </span>
                        {size && <span className="text-xs text-gray-500">· {size}</span>}
                        <span className="text-xs text-gray-600 truncate">· {d.file_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    {d.is_downloaded ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#00E87A]/15 text-[#00E87A]">
                        <CheckCircle2 className="w-3 h-3" /> Downloaded
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-300">
                        <Clock className="w-3 h-3" /> Not downloaded
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      <a
                        href={d.presigned_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-gray-300 border border-gray-600 rounded hover:bg-gray-700"
                        data-testid={`admin-docvault-view-${d.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </a>
                      <button
                        onClick={() => handleDelete(d)}
                        disabled={deletingId === d.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-red-300 border border-red-500/40 rounded hover:bg-red-500/10 disabled:opacity-50"
                        data-testid={`admin-docvault-delete-${d.id}`}
                      >
                        {deletingId === d.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showUpload && (
        <UploadModal
          companyId={companyId}
          token={token}
          onClose={() => setShowUpload(false)}
          onUploaded={() => {
            setShowUpload(false);
            fetchDocs();
          }}
        />
      )}
    </div>
  );
};

export default AdminDocVaultTab;
